import { useCallback, useRef, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { Flex, SimpleGrid, Heading, Box, useBoolean, useDisclosure } from '@chakra-ui/react';
import { AgGridColumn } from 'ag-grid-react';

import {
    createInstruments,
    columnSchemaOnMainGrid,
    validateParsedNewRowsOnMainGrid,
    deleteAllInstruments,
    updateInstruments,
    deleteInstruments,
    gridColumns,
    getHedge,
    getHedgeFilters,
    getHedgeInstruments,
    modifyHedgeProperty,
    getValidRows,
} from '../../services/hedges';
import { lenientParse, isNumberCellEmpty } from '../../services/items';

import useCommonToast from '../../hooks/useCommonToast';
import useTabularView from '../../hooks/useTabularView';
import useHistoryPush from '../../hooks/useHistoryPush';

import TopContent from './TopContent';
import HedgeCommonPropertySelect from './HedgeCommonPropertySelect';
import HedgeCommonPropertyDate from './HedgeCommonPropertyDate';
import EditAdjustmentsModal from './EditAdjustmentsModal';

import ExpandableItemWrapper from '../layout/ExpandableItemWrapper';

import DeleteAllButton from '../itemData/DeleteAllButton';
import DownloadButton from '../itemData/DownloadButton';
import UploadButton from '../itemData/UploadButton';
import AddRowsButton from '../itemData/AddRowsButton';

import DefinitionsSkeleton from '../utils/DefinitionsSkeleton';
import Error from '../utils/Error';
import DataGrid from '../grid/DataGrid';
import DataGridWrapper from '../grid/DataGridWrapper';

import CustomCheckbox from '../forms/CustomCheckbox';

const none = '-';

const Hedge = ({
    hedgeItem,
    onHedgeDelete,
    onHedgeEdit,
    onNodeRename,
    onNodeItemDelete,
    onNodeOwnershipPercentageUpdate,
    refreshMapLayer,
}) => {
    const dataTabularView = useTabularView('nope_i_dont_care_about_this', NaN);
    const editAdjustmentsModal = useDisclosure();
    const toast = useCommonToast();
    const intl = useIntl();
    const historyPush = useHistoryPush();
    const params = useParams();
    const hedgeId = Number(params.hedgeId || hedgeItem.properties.id);

    const [selectedInstrumentId, setSelectedInstrumentId] = useState(null);
    const [loadingError, setLoadingError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isDisabled, setIsDisabled] = useState(false);
    const [hedge, setHedge] = useState({
        description: '',
        bookId: null,
        pricingPoint: '',
        itemTypeId: null,
    });
    const [afterUpload, setAfterUpload] = useState(null);

    const [primaryCommoditiesOptions, setPrimaryCommoditiesOptions] = useState([]);
    const [instrumentTypeOptions, setInstrumentTypeOptions] = useState([]);
    const [counterpartiesOptions, setCounterpartiesOptions] = useState([]);
    const [secondaryCommoditiesOptions, setSecondaryCommoditiesOptions] = useState([]);
    const [quantityTypesOptions, setQuantityTypesOptions] = useState([]);
    const [masterAgreementsOptions, setMasterAgreementsOptions] = useState([]);
    const [peakPeriodsOptions, setPeakPeriodsOptions] = useState([]);

    const [initialValues, setInitialValues] = useState({
        primaryCommodities: [],
        instrumentType: none,
        counterparties: [],
        secondaryCommodities: [],
        quantityTypes: [],
        masterAgreements: [],
        dates: [],
    });

    const initialTouched = {
        primaryCommodities: true,
        instrumentType: true,
        counterparties: true,
        secondaryCommodities: true,
        quantityTypes: true,
        masterAgreements: true,
        dates: true,
    };

    const gridApi = useRef(null);
    const [isExpanded, setIsExpanded] = useBoolean();
    const [columns, setColumns] = useState([]);

    const fetchInstrumentsByType = useCallback(
        async (instrumentTypeId) => {
            const data = await getHedgeInstruments(hedgeId, instrumentTypeId);

            const maybeAddMultiple = (list) => (list.length > 1 ? list.concat('multiple') : list);

            setInitialValues((prev) => ({
                ...prev,
                primaryCommodities: maybeAddMultiple(data.primaryCommodityIds),
                counterparties: maybeAddMultiple(data.counterPartyIds),
                secondaryCommodities: maybeAddMultiple(data.secondaryCommodityIds),
                quantityTypes: maybeAddMultiple(data.quantityTypeIds),
                masterAgreements: maybeAddMultiple(data.masterAgreementIds),
                dates: maybeAddMultiple(data.dates),
            }));

            setColumns(data.columns);

            return data;
        },
        [hedgeId]
    );

    useEffect(() => {
        setIsLoading(true);
        setLoadingError(false);

        Promise.all([getHedge(hedgeId), getHedgeFilters(hedgeId)])
            .then(([hedgeProperties, commonProperties]) => {
                setHedge(hedgeProperties);

                setPrimaryCommoditiesOptions(commonProperties.primaryCommodities);
                setCounterpartiesOptions(commonProperties.counterparties);
                setSecondaryCommoditiesOptions(commonProperties.secondaryCommodities);
                setQuantityTypesOptions(commonProperties.quantityTypes);
                setMasterAgreementsOptions(commonProperties.masterAgreements);
                setPeakPeriodsOptions(commonProperties.peakPeriods);

                const defaultInstrumentType = commonProperties.instumentTypes.find((elem) => elem.instrumentsCount > 0);

                setInstrumentTypeOptions(
                    commonProperties.instumentTypes.map((elem) => {
                        let suffix = '';
                        if (elem.instrumentsCount > 0) {
                            suffix = intl.formatMessage({ id: 'hedge_instrument_type_has_data' });
                        }

                        return {
                            ...elem,
                            description: elem.description + suffix,
                            code: elem.description,
                        };
                    })
                );

                setInitialValues((prev) => ({
                    ...prev,
                    instrumentType: defaultInstrumentType
                        ? defaultInstrumentType.id
                        : commonProperties.defaultInstrumentTypeId,
                }));

                return fetchInstrumentsByType(
                    defaultInstrumentType ? defaultInstrumentType.id : commonProperties.defaultInstrumentTypeId
                );
            })
            .catch(() => setLoadingError(true))
            .finally(() => setIsLoading(false));
    }, [hedgeId, fetchInstrumentsByType, intl, afterUpload]);

    const toggleMultiple = (key) => {
        setInitialValues((prev) => {
            const oldValue = prev[key];

            return {
                ...prev,
                [key]: oldValue.includes('multiple')
                    ? oldValue.filter((c) => c !== 'multiple')
                    : oldValue.concat('multiple'),
            };
        });
    };

    const updateHedgeProperty = async (propertyName, propertyValue) => {
        setIsDisabled(true);

        try {
            if (propertyName === 'instrumentType') {
                setInitialValues((prev) => ({
                    ...prev,
                    instrumentType: propertyValue,
                }));

                await fetchInstrumentsByType(propertyValue);
            } else {
                setInitialValues((prev) => ({
                    ...prev,
                    [propertyName]: [propertyValue],
                }));

                const isRequired = propertyName === 'primaryCommodities' || propertyName === 'quantityTypes';

                if ((isRequired && propertyValue !== -1) || !isRequired) {
                    await modifyHedgeProperty(hedgeId, {
                        instrumentType: initialValues.instrumentType,
                        propertyName,
                        propertyValue,
                    });

                    gridApi.current.refreshServerSideStore({
                        purge: true,
                    });
                }
            }
        } catch (error) {
            if (error.response.status !== 401) {
                const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        } finally {
            setIsDisabled(false);
        }
    };

    const validateField = (value) => {
        return !value.includes('multiple') && (value.length === 0 || value.includes(-1));
    };

    const validateForm = (values) => {
        let errors = {};

        if (validateField(values.primaryCommodities)) {
            errors.primaryCommodities = intl.formatMessage(
                { id: 'common_forms_validation_required' },
                { label: 'Primary Commodity' }
            );
        }

        if (validateField(values.quantityTypes)) {
            errors.quantityTypes = intl.formatMessage(
                { id: 'common_forms_validation_required' },
                { label: 'Quantity type' }
            );
        }

        return errors;
    };

    const openEditAdjustmentModal = (instrumentId) => {
        setSelectedInstrumentId(instrumentId);
        editAdjustmentsModal.onOpen();
    };

    const closeEditAdjustmentModal = () => {
        setSelectedInstrumentId(null);
        editAdjustmentsModal.onClose();
    };

    // @todo this is called twice on Instrument Type change
    const getServerSideData = useCallback(
        async (params) => {
            const page = params.request.startRow / 500 + 1;
            const sortColumn = params.request.sortModel[0] || null;

            const payload = {
                page,
                sortColumn: sortColumn ? sortColumn.colId : null,
                sortByDescending: sortColumn ? sortColumn.sort === 'desc' : null,
                instrumentTypeId: initialValues.instrumentType,
            };

            const data = await getHedgeInstruments(hedgeId, payload);

            if (data.instruments.totalCount === 0) {
                params.api.showNoRowsOverlay();
            }

            params.success({
                rowData: data.instruments.items,
                rowCount: data.instruments.totalCount,
            });
        },
        [initialValues.instrumentType, hedgeId]
    );

    const formatRows = (parsedData) => {
        return parsedData.map((row) => {
            return {
                ...row,

                itemId: hedgeId,

                // @todo AddRowsButton uses a client side grid that doesn't use columnTypes
                // which contain valueSetters needed for parsing the incoming data.
                // This requires us to convert useFlag to boolean here.
                useFlag: Number(row.useFlag) === 1 ? true : false,

                // apply lenient parse to dates,
                startDate: lenientParse(row.startDate).toISOString(),
                endDate: lenientParse(row.endDate).toISOString(),
                expiryDate: row.expiryDate ? lenientParse(row.expiryDate).toISOString() : null,
                transactionDate: row.transactionDate ? lenientParse(row.transactionDate).toISOString() : null,

                instrumentTypeId: instrumentTypeOptions.find(({ code }) => code === row.instrumentTypeId)?.id,
                primaryCommodityId: primaryCommoditiesOptions.find(
                    ({ description }) => description === row.primaryCommodityId
                )?.id,
                peakPeriodId: peakPeriodsOptions.find(({ description }) => description === row.peakPeriodId)?.id,
                peakPeriod: peakPeriodsOptions.find(({ description }) => description === row.peakPeriodId)?.description,

                quantityTypeId: quantityTypesOptions.find(({ description }) => description === row.quantityTypeId)?.id,
                counterPartyId: counterpartiesOptions.find(({ description }) => description === row.counterPartyId)?.id,
                masterAgreementId: masterAgreementsOptions.find(
                    ({ description }) => description === row.masterAgreementId
                )?.id,
                secondaryCommodityId: secondaryCommoditiesOptions.find(
                    ({ description }) => description === row.secondaryCommodityId
                )?.id,

                price1: isNumberCellEmpty(row.price1),
                price2: isNumberCellEmpty(row.price2),
                price3: isNumberCellEmpty(row.price3),
                price4: isNumberCellEmpty(row.price4),
                price5: isNumberCellEmpty(row.price5),
                price6: isNumberCellEmpty(row.price6),
                quantity: isNumberCellEmpty(row.quantity),
            };
        });
    };

    const updateServerSideData = async (rowIds) => {
        const rows = rowIds.map((id) => gridApi.current.getRowNode(id));
        const validRows = getValidRows(rows);

        if (validRows.length === 0) return;

        try {
            // @todo the `peakPeriod` isn't correct - it's the old value, because we're only working with `peakPeriodId` (the dropdown)
            // the BE shouldn't be even sending it, but at least it doesn't read it when sending the /modify-instruments endpoint
            await updateInstruments(validRows);

            fetchInstrumentsByType(initialValues.instrumentType);

            toast(intl.formatMessage({ id: 'hedge_save_changes_success' }));
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: intl.formatMessage({ id: 'common_generic_saving_error' }),
                });
            }
        }
    };

    // Update instrument array with the new instrument count and update suffix
    const updateInstrumentTypeOptions = (rows, type) => {
        const suffix = intl.formatMessage({ id: 'hedge_instrument_type_has_data' });

        const instrumentOccurrences = rows
            .map((elem) => elem.instrumentTypeId)
            .reduce(function (prev, curr) {
                if (type === 'add') {
                    if (prev[curr]) {
                        prev[curr] += 1;
                    } else {
                        prev[curr] = 1;
                    }
                } else {
                    if (prev[curr]) {
                        prev[curr] -= 1;
                    } else {
                        prev[curr] = -1;
                    }
                }
                return prev;
            }, {});

        setInstrumentTypeOptions(([...prev]) =>
            prev.map((elem) => {
                if (instrumentOccurrences[elem.id]) {
                    return {
                        ...elem,
                        instrumentsCount: elem.instrumentsCount + instrumentOccurrences[elem.id],
                        description:
                            elem.instrumentsCount + instrumentOccurrences[elem.id] > 0 ? elem.code + suffix : elem.code,
                    };
                }
                return elem;
            })
        );
    };

    const onDeleteRow = async (rowIds) => {
        const nodes = rowIds.map((id) => gridApi.current.getRowNode(id).data);
        const instrumentIds = nodes.map((elem) => elem.instrumentId);

        try {
            await deleteInstruments(hedgeId, instrumentIds);
            updateInstrumentTypeOptions(nodes, 'delete');

            toast(intl.formatMessage({ id: 'common_delete_grid_msg' }, { n: instrumentIds.length }));

            gridApi.current.refreshServerSideStore({
                purge: true,
            });
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: intl.formatMessage({ id: 'common_generic_delete_error' }),
                });
            }
        }
    };

    const onDeleteAllSuccess = () => {
        setInstrumentTypeOptions(([...prev]) =>
            prev.map((elem) => {
                return {
                    ...elem,
                    instrumentsCount: 0,
                    description: elem.code,
                };
            })
        );
    };

    const onAddRows = async (newRows) => {
        const { setIsLoading, addRows } = dataTabularView;

        const successMessage = intl.formatMessage({ id: 'common_generic_item_change_success' });
        const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });

        try {
            setIsLoading(true);

            await createInstruments(newRows);

            gridApi.current.refreshServerSideStore({
                purge: true,
            });

            setIsLoading(false);
            addRows.onClose();
            updateInstrumentTypeOptions(newRows, 'add');

            toast(successMessage);
        } catch (error) {
            setIsLoading(false);

            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        }
    };

    const getColumnDisplayName = (field, columns) => {
        const fieldName = field.toLowerCase();
        const col = columns.find(({ name }) => name.toLowerCase() === fieldName);

        return col ? col.displayName : field;
    };

    const getUserGeneratedColumns = (type, n, isExpandable, extraColumnConfig) => {
        return Array.from({ length: n }).map((_, i) => {
            const field = type + (i + 1);

            const headerName = getColumnDisplayName(field, columns);
            const hide = !headerName || (isExpandable ? !isExpanded : false) || !visibleColumns.includes(field);

            return {
                field,
                headerName,
                hide,
                resizable: true,
                minWidth: 150,
                ...extraColumnConfig,
            };
        });
    };

    const visibleColumns = gridColumns
        .filter(({ field }) => {
            const fieldName = field.toLowerCase();

            return columns.some(({ name }) => name.toLowerCase() === fieldName);
        })
        .map((column) => column.field);

    const mainGridColumns = [
        {
            type: 'checkbox',
            field: 'useFlag',
            headerName: intl.formatMessage({ id: 'hedge_grid_use_flag' }),
            hide: !visibleColumns.includes('useFlag'),
            resizable: true,
        },
        {
            field: 'primaryCommodityId',
            type: 'select',
            cellEditorParams: { options: primaryCommoditiesOptions },
            headerName: intl.formatMessage({ id: 'hedge_primary_commodity' }),
            hide:
                !initialValues.primaryCommodities.includes('multiple') ||
                !visibleColumns.includes('primaryCommodityId'),
            resizable: true,
            minWidth: 180,
        },
        {
            field: 'startDate',
            type: 'date',
            headerName: intl.formatMessage({ id: 'common_grid_start_date' }),
            hide: !visibleColumns.includes('startDate'),
            resizable: true,
            sortable: true,
        },
        {
            field: 'endDate',
            type: 'date',
            headerName: intl.formatMessage({ id: 'common_grid_end_date' }),
            hide: !visibleColumns.includes('endDate'),
            resizable: true,
            sortable: true,
        },
        {
            field: 'peakPeriodId',
            type: 'select',
            cellEditorParams: { options: peakPeriodsOptions, required: false },
            headerName: intl.formatMessage({ id: 'hedge_grid_peak_period' }),
            hide: !visibleColumns.includes('peakPeriodId'),
            resizable: true,
            sortable: true,
            minWidth: 180,
        },
        ...getUserGeneratedColumns('price', 6, false, { type: 'number' }),
        {
            field: 'quantity',
            type: 'number',
            headerName: intl.formatMessage({ id: 'hedge_grid_quantity' }),
            hide: !visibleColumns.includes('quantity'),
            resizable: true,
            sortable: true,
        },
        {
            field: 'quantityTypeId',
            type: 'select',
            cellEditorParams: { options: quantityTypesOptions },
            headerName: intl.formatMessage({ id: 'hedge_quantity_type' }),
            hide: !initialValues.quantityTypes.includes('multiple') || !visibleColumns.includes('quantityTypeId'),
            resizable: true,
            minWidth: 180,
        },
        {
            field: 'counterPartyId',
            type: 'select',
            cellEditorParams: { options: counterpartiesOptions, required: false },
            headerName: intl.formatMessage({ id: 'hedge_counterparty' }),
            hide: !initialValues.counterparties.includes('multiple') || !visibleColumns.includes('counterPartyId'),
            resizable: true,
            minWidth: 180,
        },
        {
            field: 'transactionDate',
            type: 'date',
            cellEditorParams: { nullable: true },
            headerName: intl.formatMessage({ id: 'hedge_transaction_date' }),
            hide: !initialValues.dates.includes('multiple') || !visibleColumns.includes('transactionDate'),
            resizable: true,
        },
        {
            field: 'expiryDate',
            type: 'date',
            cellEditorParams: { nullable: true },
            headerName: intl.formatMessage({ id: 'hedge_grid_expiry_date' }),
            hide: !visibleColumns.includes('expiryDate'),
            resizable: true,
        },
        {
            field: 'masterAgreementId',
            type: 'select',
            cellEditorParams: { options: masterAgreementsOptions, required: false },
            headerName: intl.formatMessage({ id: 'hedge_master_agreement' }),
            hide: !initialValues.masterAgreements.includes('multiple') || !visibleColumns.includes('masterAgreementId'),
            resizable: true,
            minWidth: 180,
        },
        {
            field: 'secondaryCommodityId',
            type: 'select',
            cellEditorParams: { options: secondaryCommoditiesOptions, required: false },
            headerName: intl.formatMessage({ id: 'hedge_secondary_commodity' }),
            hide:
                !initialValues.secondaryCommodities.includes('multiple') ||
                !visibleColumns.includes('secondaryCommodityId'),
            resizable: true,
            minWidth: 180,
        },
        {
            field: 'sourceLocation',
            headerName: intl.formatMessage({ id: 'hedge_grid_source_location' }),
            hide: !visibleColumns.includes('sourceLocation'),
            resizable: true,
        },
        {
            field: 'systemIdentifier',
            headerName: intl.formatMessage({ id: 'hedge_grid_system_identifier' }),
            hide: !visibleColumns.includes('systemIdentifier'),
            resizable: true,
        },
        ...getUserGeneratedColumns('expansion', 20, true),
    ];

    let addCols = mainGridColumns.map((c) => ({
        field: c.field,
        headerName: c.headerName,
    }));

    addCols.splice(1, 0, {
        field: 'instrumentTypeId',
        headerName: intl.formatMessage({ id: 'hedge_instrument_type' }),
    });

    return (
        <ExpandableItemWrapper>
            {isLoading ? (
                <DefinitionsSkeleton />
            ) : loadingError ? (
                <Box m={6}>
                    <Error primaryId="common_error" secondaryId="common_loading_error" additionalText="hedge" />
                </Box>
            ) : (
                <>
                    <TopContent
                        portfolio={hedgeItem}
                        hedge={hedge}
                        onHedgeDelete={(id) => {
                            if (!hedgeItem) {
                                onHedgeDelete(id);
                                historyPush('/hedges-contracts');
                            } else {
                                onNodeItemDelete();
                            }
                        }}
                        onHedgeEdit={(payload) => {
                            if (!hedgeItem) {
                                onHedgeEdit({ ...payload, securityId: hedge.securityId });
                            } else {
                                onNodeRename(payload.description);
                            }
                        }}
                        onNodeOwnershipPercentageUpdate={onNodeOwnershipPercentageUpdate}
                        refreshMapLayer={refreshMapLayer}
                    />

                    <Heading m={6} as="h2" variant="h2">
                        <FormattedMessage id="hedge_transaction_specifics" />
                    </Heading>
                    <Formik
                        enableReinitialize
                        initialTouched={initialTouched}
                        initialValues={initialValues}
                        validate={validateForm}
                    >
                        {() => {
                            return (
                                <Form>
                                    <Flex
                                        m={6}
                                        justify="space-between"
                                        flexWrap="wrap"
                                        direction={{ base: 'column', xl: 'row' }}
                                        align={{ base: 'unset', xl: 'center' }}
                                    >
                                        <Flex direction={{ base: 'column', xl: 'row' }}>
                                            <HedgeCommonPropertySelect
                                                placeholder={null}
                                                name="instrumentType"
                                                label={<FormattedMessage id="hedge_instrument_type" />}
                                                isDisabled={isDisabled}
                                                options={instrumentTypeOptions}
                                                info={<FormattedMessage id="hedge_instrument_type_popover" />}
                                                footerLabel={
                                                    <FormattedMessage
                                                        id="hedge_instrument_select_footer"
                                                        values={{
                                                            haveData: instrumentTypeOptions.filter(
                                                                (elem) => elem.instrumentsCount > 0
                                                            ).length,
                                                            all: instrumentTypeOptions.length,
                                                        }}
                                                    />
                                                }
                                                onChange={(value) => updateHedgeProperty('instrumentType', value)}
                                                minWidth="160px"
                                            />
                                        </Flex>
                                        <Flex direction={{ base: 'column', xl: 'row' }}>
                                            <CustomCheckbox
                                                minWidth="fit-content"
                                                isChecked={isExpanded}
                                                onChange={setIsExpanded.toggle}
                                                mr={5}
                                            >
                                                <FormattedMessage id="hedge_expand_details" />
                                            </CustomCheckbox>

                                            <DownloadButton
                                                type="hedges"
                                                itemId={hedgeId}
                                                filters={{
                                                    instrumentTypeId: initialValues.instrumentType,
                                                }}
                                                hasLabel={true}
                                                itemType="hedge"
                                                m={{ base: '0 0 16px 0', xl: '0 16px 0 0' }}
                                            />

                                            <UploadButton
                                                type="hedges"
                                                itemId={hedgeId}
                                                hasLabel={true}
                                                m={{ base: '16px 0', xl: '0 16px 0 0' }}
                                                onAfterUploadChange={() => setAfterUpload((prev) => !prev)}
                                            />

                                            <DeleteAllButton
                                                subItemId={hedgeId}
                                                deleteCallback={deleteAllInstruments}
                                                successCallback={onDeleteAllSuccess}
                                                refetchData={() => {
                                                    gridApi.current.refreshServerSideStore({
                                                        purge: true,
                                                    });
                                                }}
                                                isInTabularTab={true}
                                            />

                                            <AddRowsButton
                                                modal={dataTabularView.addRows}
                                                isLoading={dataTabularView.isLoading}
                                                disabled={false}
                                                tabIndex={0}
                                                validate={validateParsedNewRowsOnMainGrid}
                                                format={formatRows}
                                                columns={addCols}
                                                columnSchema={{
                                                    ...columnSchemaOnMainGrid,
                                                    instrumentTypeId: {
                                                        ...columnSchemaOnMainGrid.instrumentTypeId,
                                                        constraints: instrumentTypeOptions.map((type) => type.code),
                                                    },
                                                    peakPeriodId: {
                                                        ...columnSchemaOnMainGrid.peakPeriodId,
                                                        constraints: peakPeriodsOptions.map((type) => type.description),
                                                    },
                                                    primaryCommodityId: {
                                                        ...columnSchemaOnMainGrid.primaryCommodityId,
                                                        constraints: primaryCommoditiesOptions.map(
                                                            (type) => type.description
                                                        ),
                                                    },
                                                    quantityTypeId: {
                                                        ...columnSchemaOnMainGrid.quantityTypeId,
                                                        constraints: quantityTypesOptions.map(
                                                            (type) => type.description
                                                        ),
                                                    },
                                                    counterPartyId: {
                                                        ...columnSchemaOnMainGrid.counterPartyId,
                                                        constraints: counterpartiesOptions.map(
                                                            (type) => type.description
                                                        ),
                                                    },
                                                    masterAgreementId: {
                                                        ...columnSchemaOnMainGrid.masterAgreementId,
                                                        constraints: masterAgreementsOptions.map(
                                                            (type) => type.description
                                                        ),
                                                    },
                                                    secondaryCommodityId: {
                                                        ...columnSchemaOnMainGrid.secondaryCommodityId,
                                                        constraints: secondaryCommoditiesOptions.map(
                                                            (type) => type.description
                                                        ),
                                                    },
                                                }}
                                                addRows={onAddRows}
                                            />
                                        </Flex>
                                    </Flex>

                                    <SimpleGrid m={6} columns={{ xl: 3 }} spacingX={6} spacingY={5}>
                                        <HedgeCommonPropertySelect
                                            name="primaryCommodities"
                                            label={<FormattedMessage id="hedge_primary_commodity" />}
                                            isDisabled={isDisabled}
                                            options={primaryCommoditiesOptions}
                                            onMultipleToggleChange={() => toggleMultiple('primaryCommodities')}
                                            onChange={(value) => updateHedgeProperty('primaryCommodities', value)}
                                        />

                                        <HedgeCommonPropertySelect
                                            name="counterparties"
                                            label={<FormattedMessage id="hedge_counterparty" />}
                                            isDisabled={isDisabled}
                                            options={counterpartiesOptions}
                                            onMultipleToggleChange={() => toggleMultiple('counterparties')}
                                            onChange={(value) => updateHedgeProperty('counterparties', value)}
                                        />

                                        <HedgeCommonPropertySelect
                                            name="secondaryCommodities"
                                            label={<FormattedMessage id="hedge_secondary_commodity" />}
                                            isDisabled={isDisabled}
                                            options={secondaryCommoditiesOptions}
                                            onMultipleToggleChange={() => toggleMultiple('secondaryCommodities')}
                                            onChange={(value) => updateHedgeProperty('secondaryCommodities', value)}
                                        />

                                        <HedgeCommonPropertySelect
                                            name="quantityTypes"
                                            label={<FormattedMessage id="hedge_quantity_type" />}
                                            isDisabled={isDisabled}
                                            options={quantityTypesOptions}
                                            onMultipleToggleChange={() => toggleMultiple('quantityTypes')}
                                            onChange={(value) => updateHedgeProperty('quantityTypes', value)}
                                        />

                                        <HedgeCommonPropertySelect
                                            name="masterAgreements"
                                            label={<FormattedMessage id="hedge_master_agreement" />}
                                            isDisabled={isDisabled}
                                            options={masterAgreementsOptions}
                                            onMultipleToggleChange={() => toggleMultiple('masterAgreements')}
                                            onChange={(value) => updateHedgeProperty('masterAgreements', value)}
                                        />

                                        <HedgeCommonPropertyDate
                                            name="dates"
                                            label={<FormattedMessage id="hedge_transaction_date" />}
                                            isDisabled={isDisabled}
                                            onMultipleToggleChange={() => toggleMultiple('dates')}
                                            onChange={(value) => updateHedgeProperty('dates', value)}
                                        />
                                    </SimpleGrid>
                                </Form>
                            );
                        }}
                    </Formik>

                    <Box mx={6} my={9}>
                        <DataGridWrapper>
                            <DataGrid
                                enableBrowserTooltips={true}
                                serverSideDatasource={{ getRows: getServerSideData }}
                                onGridReady={(params) => (gridApi.current = params.api)}
                                onDataChange={updateServerSideData}
                                onDelete={onDeleteRow}
                                isInitialRequestSent={true}
                                rowModelType="serverSide"
                                serverSideStoreType="partial"
                                paginationPageSize={500}
                                cacheBlockSize={500}
                                onCellKeyPress={(params) => {
                                    if (params.event.key === 'Enter' && params.column.colId === 'adjustmentEdit') {
                                        openEditAdjustmentModal(params.value);
                                    }
                                }}
                            >
                                {mainGridColumns.map((def) => (
                                    <AgGridColumn key={def.field} {...def} />
                                ))}

                                <AgGridColumn
                                    type="button"
                                    colId="adjustmentEdit"
                                    field="instrumentId"
                                    headerName={intl.formatMessage({ id: 'hedge_adjustments_modal_title' })}
                                    resizable
                                    onCellClicked={(params) => {
                                        openEditAdjustmentModal(params.value);
                                    }}
                                />
                            </DataGrid>
                        </DataGridWrapper>
                    </Box>

                    {editAdjustmentsModal.isOpen && (
                        <EditAdjustmentsModal instrumentId={selectedInstrumentId} onClose={closeEditAdjustmentModal} />
                    )}
                </>
            )}
        </ExpandableItemWrapper>
    );
};

export default Hedge;
